import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
}

const SITE_NAME = '兄弟維修';
const BASE_URL = 'https://www.brotherrepair.com.tw';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-cover.jpg`;

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    // Title
    document.title = `${title} | ${SITE_NAME}`;

    // Helper to set meta
    const setMeta = (selector: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const attr = selector.match(/\[(\w+)="([^"]+)"\]/);
        if (attr) el.setAttribute(attr[1], attr[2]);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);
    if (noIndex) setMeta('meta[name="robots"]', 'noindex,nofollow');

    // Canonical
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
    setLink('canonical', canonicalUrl);

    // OG
    setMeta('meta[property="og:title"]', `${title} | ${SITE_NAME}`);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:type"]', ogType);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:image"]', ogImage);
    setMeta('meta[property="og:site_name"]', SITE_NAME);

    // Twitter
    setMeta('meta[name="twitter:title"]', `${title} | ${SITE_NAME}`);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', ogImage);

    // Last modified
    setMeta('meta[name="last-modified"]', new Date().toISOString().split('T')[0]);

    return () => {
      // Reset to default on unmount
      document.title = `${SITE_NAME}｜iPhone、iPad、Mac 專業維修服務`;
    };
  }, [title, description, keywords, canonical, ogImage, ogType, noIndex]);
}
